import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, X, ArchiveRestore, Loader } from 'lucide-react';
import { motion } from 'framer-motion';
import { useRecoveryLogic } from './hooks/useRecoveryLogic';
import { Button } from '../../../components/ui/button';
import { TableCell, TableRow } from '../../../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';

// Create motion-enabled TableRow component
const MotionTableRow = motion(TableRow);

const RecoveryFileListItem: React.FC = () => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const {
    firstRecord,
    isRecovering,
    progress,
    hasMultiple,
    recentRecoverableFiles,
    hasRecoverableFiles,
    handleRecover,
    handleDismiss,
    getTimeAgo,
  } = useRecoveryLogic();

  const handleRecoverClick = useCallback(() => {
    if (firstRecord) {
      handleRecover(firstRecord);
    }
  }, [firstRecord, handleRecover]);

  const handleDismissClick = useCallback(
    (e: React.MouseEvent, fileId: string) => {
      e.stopPropagation();
      handleDismiss(fileId);
    },
    [handleDismiss],
  );

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  // Don't render if no recent recoverable files
  if (!hasRecoverableFiles) {
    return null;
  }

  return (
    <MotionTableRow
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.98 }}
      transition={{
        opacity: { duration: 0.1, ease: 'easeOut' },
        scale: { duration: 0.1, ease: 'easeOut' },
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleRecoverClick}
      className="group relative transition-colors duration-150 cursor-pointer bg-white dark:bg-background hover:!bg-muted/50 border-b border-border/30"
    >
      {/* Name Column - matches FileListItem structure */}
      <TableCell className="py-4 px-4">
        <div className="flex items-center gap-3 min-w-0">
          {/* Thumbnail placeholder - matches FileListItem dimensions */}
          <div className="relative flex-shrink-0 h-9 w-16 rounded bg-muted flex items-center justify-center">
            <div className="relative w-4 h-4">
              <ArchiveRestore
                className={`absolute w-4 h-4 text-muted-foreground transition-all duration-200 ease-out ${
                  isHovered ? 'opacity-100' : 'opacity-0'
                }`}
              />
              <AlertTriangle
                className={`absolute w-4 h-4 text-muted-foreground transition-all duration-200 ease-out ${
                  isHovered ? 'opacity-0' : 'opacity-60'
                }`}
              />
            </div>
          </div>

          {/* Title and Metadata - matches FileListItem */}
          <div className="flex flex-col gap-1.5 min-w-0 flex-1">
            <span className="text-sm font-medium truncate leading-snug">
              {hasMultiple
                ? t('library.recovery.title.multiple', {
                    count: recentRecoverableFiles.length,
                  })
                : t('library.recovery.title.single')}
            </span>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground leading-none">
              {isRecovering ? (
                <div className="flex items-center gap-1.5">
                  <Loader className="h-3 w-3 animate-spin" />
                  <span>{Math.round(progress)}%</span>
                </div>
              ) : (
                <span>
                  {isHovered
                    ? t('library.recovery.status.finalize')
                    : t('library.recovery.status.interrupted')}
                </span>
              )}
            </div>
          </div>
        </div>
      </TableCell>

      {/* Date Column - matches FileListItem */}
      <TableCell className="py-4 px-4 w-32 text-right">
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {firstRecord && getTimeAgo(firstRecord.startedAt)}
        </span>
      </TableCell>

      {/* Action Menu Column - matches FileListItem */}
      <TableCell className="py-4 w-10 pr-4">
        <div className="transition-all duration-200 flex justify-end opacity-0 group-hover:opacity-100">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0"
                  onClick={(e) => {
                    if (firstRecord) {
                      handleDismissClick(e, firstRecord.fileId);
                    }
                  }}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('library.recovery.dismiss')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </TableCell>
    </MotionTableRow>
  );
};

export default RecoveryFileListItem;
